import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Package, Plus, Search, X, Pencil, Trash2, AlertTriangle, Boxes, Coins, TrendingDown,
  Scissors, ArrowDownToLine, ArrowUpFromLine, History, Loader2,
} from "lucide-react";

const CATS = [
  { key: "cuir", label: "Cuir" }, { key: "simili", label: "Simili" }, { key: "mousse", label: "Mousse" },
  { key: "tissu", label: "Tissu / toile" }, { key: "fil", label: "Fil & mercerie" }, { key: "autre", label: "Autre" },
];
const CAT_COLOR = { cuir: "bg-cognac-100 text-cognac-700", simili: "bg-slate-100 text-slate-600", mousse: "bg-amber-100 text-amber-700", tissu: "bg-sky-100 text-sky-700", fil: "bg-emerald-100 text-emerald-700", autre: "bg-violet-100 text-violet-700" };
const UNITS = ["m", "m²", "u", "kg", "rouleau"];
const catLabel = (k) => (CATS.find((c) => c.key === k) || {}).label || k;
const euro = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function Materials() {
  const [materials, setMaterials] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [editing, setEditing] = useState(null); // material or {} for new
  const [detail, setDetail] = useState(null); // material id open in drawer

  const load = () => api.get("/inventory").then((r) => setMaterials(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = materials.filter((m) => {
    if (cat !== "all" && m.category !== cat) return false;
    if (q && !`${m.name} ${m.reference} ${m.supplier}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const lowStock = materials.filter((m) => m.threshold > 0 && m.stock <= m.threshold);
  const stockValue = materials.reduce((s, m) => s + (m.stock || 0) * (m.price || 0), 0);

  const remove = async (m) => {
    if (!window.confirm(`Supprimer « ${m.name} » et son historique ?`)) return;
    await api.delete(`/inventory/${m.id}`); toast.success("Matière supprimée"); load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><Package className="w-5 h-5 text-cognac-400" /></div>
          <div>
            <h1 className="text-3xl font-display font-bold text-navy">Matières & stocks</h1>
            <p className="text-slate-500">Suivi des stocks, lots et chutes pour piloter vos approvisionnements</p>
          </div>
        </div>
        <button onClick={() => setEditing({})} data-testid="new-material-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cognac-500 text-white font-semibold hover:bg-cognac-600"><Plus className="w-4 h-4" /> Nouvelle matière</button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Kpi icon={Boxes} color="sky" label="Références" value={materials.length} testid="mat-kpi-count" />
        <Kpi icon={Coins} color="emerald" label="Valeur du stock" value={euro(stockValue)} testid="mat-kpi-value" />
        <Kpi icon={TrendingDown} color={lowStock.length ? "rose" : "slate"} label="Alertes stock faible" value={lowStock.length} testid="mat-kpi-low" />
      </div>

      {lowStock.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3" data-testid="low-stock-banner">
          <div className="flex items-center gap-2 text-rose-700 font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> {lowStock.length} matière{lowStock.length > 1 ? "s" : ""} sous le seuil</div>
          <div className="mt-1 text-xs text-rose-600">{lowStock.map((m) => `${m.name} (${m.stock} ${m.unit})`).join(" · ")}</div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-2 border-b border-slate-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="mat-search" placeholder="Rechercher une matière, une réf, un fournisseur…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cognac-400" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="mat-filter-cat" className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
            <option value="all">Toutes catégories</option>
            {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3 font-medium">Matière</th><th className="px-4 py-3 font-medium">Catégorie</th>
              <th className="px-4 py-3 font-medium">Fournisseur</th><th className="px-4 py-3 font-medium text-right">Stock</th>
              <th className="px-4 py-3 font-medium text-right">Prix</th><th className="px-4 py-3 font-medium"></th>
            </tr></thead>
            <tbody>
              {filtered.map((m) => {
                const low = m.threshold > 0 && m.stock <= m.threshold;
                return (
                  <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" data-testid={`mat-row-${m.id}`} onClick={() => setDetail(m.id)}>
                    <td className="px-4 py-3"><div className="font-semibold text-navy">{m.name}</div><div className="text-[11px] text-slate-400">{m.reference || "—"} · {(m.offcuts || []).length} chute{(m.offcuts || []).length > 1 ? "s" : ""}</div></td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${CAT_COLOR[m.category] || CAT_COLOR.autre}`}>{catLabel(m.category)}</span></td>
                    <td className="px-4 py-3 text-slate-500">{m.supplier || "—"}</td>
                    <td className="px-4 py-3 text-right"><span className={`font-semibold tabular ${low ? "text-rose-600" : "text-navy"}`}>{m.stock} {m.unit}</span>{low && <AlertTriangle className="inline w-3.5 h-3.5 text-rose-500 ml-1" />}<div className="text-[11px] text-slate-400">seuil {m.threshold} {m.unit}</div></td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular">{euro(m.price)}/{m.unit}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setEditing(m)} data-testid={`mat-edit-${m.id}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(m)} data-testid={`mat-delete-${m.id}`} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Aucune matière. Cliquez sur « Nouvelle matière » pour commencer.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <MaterialModal material={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detail && <MaterialDrawer id={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

const CLR = {
  sky: { bg: "bg-sky-50", fg: "text-sky-600" },
  emerald: { bg: "bg-emerald-50", fg: "text-emerald-600" },
  rose: { bg: "bg-rose-50", fg: "text-rose-600" },
  slate: { bg: "bg-slate-100", fg: "text-slate-500" },
  cognac: { bg: "bg-cognac-50", fg: "text-cognac-600" },
  violet: { bg: "bg-violet-50", fg: "text-violet-600" },
};

const Kpi = ({ icon: Icon, color, label, value, testid }) => {
  const c = CLR[color] || CLR.slate;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-3" data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.bg}`}><Icon className={`w-5 h-5 ${c.fg}`} /></div>
      <div><div className="text-[11px] text-slate-500">{label}</div><div className="text-2xl font-display font-bold text-navy tabular">{value}</div></div>
    </div>
  );
};

function MaterialModal({ material, onClose, onSaved }) {
  const isNew = !material.id;
  const [f, setF] = useState({ reference: "", name: "", category: "cuir", unit: "m", supplier: "", stock: 0, threshold: 0, price: 0, note: "", ...material });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const payload = { reference: f.reference, name: f.name, category: f.category, unit: f.unit, supplier: f.supplier, stock: Number(f.stock) || 0, threshold: Number(f.threshold) || 0, price: Number(f.price) || 0, note: f.note };
      if (isNew) await api.post("/inventory", payload); else await api.put(`/inventory/${material.id}`, payload);
      toast.success(isNew ? "Matière créée" : "Matière mise à jour"); onSaved();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="material-modal">
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-display font-bold text-navy">{isNew ? "Nouvelle matière" : "Modifier la matière"}</h3><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Référence"><input value={f.reference} onChange={(e) => set("reference", e.target.value)} data-testid="mat-f-ref" className={inp} placeholder="MAT-001" /></Field>
          <Field label="Fournisseur"><input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="mat-f-supplier" className={inp} /></Field>
          <Field label="Désignation" full><input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="mat-f-name" className={inp} placeholder="Cuir pleine fleur cognac" /></Field>
          <Field label="Catégorie"><select value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="mat-f-cat" className={inp}>{CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></Field>
          <Field label="Unité"><select value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="mat-f-unit" className={inp}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select></Field>
          <Field label={`Stock initial (${f.unit})`}><input type="number" value={f.stock} onChange={(e) => set("stock", e.target.value)} data-testid="mat-f-stock" className={inp} /></Field>
          <Field label={`Seuil d'alerte (${f.unit})`}><input type="number" value={f.threshold} onChange={(e) => set("threshold", e.target.value)} data-testid="mat-f-threshold" className={inp} /></Field>
          <Field label={`Prix d'achat (€/${f.unit})`} full><input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="mat-f-price" className={inp} /></Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="mat-save" className="px-4 py-2.5 rounded-xl bg-cognac-500 text-white font-semibold hover:bg-cognac-600 disabled:opacity-60 inline-flex items-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function MaterialDrawer({ id, onClose, onChanged }) {
  const [mat, setMat] = useState(null);
  const [hist, setHist] = useState({ lots: [], movements: [] });
  const [lot, setLot] = useState({ quantity: "", lot_number: "", cost: "" });
  const [mov, setMov] = useState({ quantity: "", reason: "Sortie production" });
  const [oc, setOc] = useState({ width: "", height: "", quantity: 1 });

  const load = async () => {
    const [m, h] = await Promise.all([api.get("/inventory"), api.get(`/inventory/${id}/movements`)]);
    setMat((m.data || []).find((x) => x.id === id) || null); setHist(h.data || { lots: [], movements: [] });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const refresh = async () => { await load(); onChanged?.(); };
  const addLot = async () => {
    if (!Number(lot.quantity)) { toast.error("Quantité requise"); return; }
    await api.post(`/inventory/${id}/lots`, { quantity: Number(lot.quantity), lot_number: lot.lot_number, cost: Number(lot.cost) || 0, date: new Date().toISOString().slice(0, 10) });
    setLot({ quantity: "", lot_number: "", cost: "" }); toast.success("Entrée de stock enregistrée"); refresh();
  };
  const addMov = async () => {
    const qty = Number(mov.quantity);
    if (!qty) { toast.error("Quantité requise"); return; }
    await api.post(`/inventory/${id}/movements`, { quantity: -Math.abs(qty), reason: mov.reason });
    setMov({ quantity: "", reason: "Sortie production" }); toast.success("Sortie enregistrée"); refresh();
  };
  const addOc = async () => {
    await api.post(`/inventory/${id}/offcuts`, { width: Number(oc.width) || 0, height: Number(oc.height) || 0, quantity: Number(oc.quantity) || 1 });
    setOc({ width: "", height: "", quantity: 1 }); toast.success("Chute ajoutée"); refresh();
  };
  const delOc = async (oid) => { await api.delete(`/inventory/${id}/offcuts/${oid}`); refresh(); };

  if (!mat) return null;
  const low = mat.threshold > 0 && mat.stock <= mat.threshold;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6 animate-slide-in" onClick={(e) => e.stopPropagation()} data-testid="material-drawer">
        <div className="flex items-start justify-between">
          <div>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${CAT_COLOR[mat.category] || CAT_COLOR.autre}`}>{catLabel(mat.category)}</span>
            <h3 className="mt-2 text-xl font-display font-bold text-navy">{mat.name}</h3>
            <p className="text-xs text-slate-400">{mat.reference || "—"} · {mat.supplier || "sans fournisseur"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="mt-4 rounded-xl border border-slate-200 p-4 flex items-center justify-between">
          <div><div className="text-xs text-slate-500">Stock actuel</div><div className={`text-3xl font-display font-bold tabular ${low ? "text-rose-600" : "text-navy"}`}>{mat.stock} <span className="text-base font-normal text-slate-400">{mat.unit}</span></div></div>
          <div className="text-right text-xs text-slate-400">Seuil {mat.threshold} {mat.unit}<br />Valeur {euro((mat.stock || 0) * (mat.price || 0))}</div>
        </div>

        <Section icon={ArrowDownToLine} title="Entrée de stock (lot)">
          <div className="grid grid-cols-3 gap-2">
            <input type="number" value={lot.quantity} onChange={(e) => setLot({ ...lot, quantity: e.target.value })} placeholder={`Qté (${mat.unit})`} data-testid="lot-qty" className={inpSm} />
            <input value={lot.lot_number} onChange={(e) => setLot({ ...lot, lot_number: e.target.value })} placeholder="N° lot" data-testid="lot-num" className={inpSm} />
            <input type="number" value={lot.cost} onChange={(e) => setLot({ ...lot, cost: e.target.value })} placeholder="Coût €" data-testid="lot-cost" className={inpSm} />
          </div>
          <button onClick={addLot} data-testid="lot-add" className="mt-2 w-full py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 inline-flex items-center justify-center gap-1.5"><ArrowDownToLine className="w-4 h-4" /> Ajouter l'entrée</button>
        </Section>

        <Section icon={ArrowUpFromLine} title="Sortie de stock">
          <div className="grid grid-cols-2 gap-2">
            <input type="number" value={mov.quantity} onChange={(e) => setMov({ ...mov, quantity: e.target.value })} placeholder={`Qté (${mat.unit})`} data-testid="mov-qty" className={inpSm} />
            <input value={mov.reason} onChange={(e) => setMov({ ...mov, reason: e.target.value })} placeholder="Motif" data-testid="mov-reason" className={inpSm} />
          </div>
          <button onClick={addMov} data-testid="mov-add" className="mt-2 w-full py-2 rounded-lg bg-cognac-500 text-white text-sm font-semibold hover:bg-cognac-600 inline-flex items-center justify-center gap-1.5"><ArrowUpFromLine className="w-4 h-4" /> Enregistrer la sortie</button>
        </Section>

        <Section icon={Scissors} title={`Chutes réutilisables (${(mat.offcuts || []).length})`}>
          <div className="grid grid-cols-3 gap-2">
            <input type="number" value={oc.width} onChange={(e) => setOc({ ...oc, width: e.target.value })} placeholder="Larg. cm" data-testid="oc-w" className={inpSm} />
            <input type="number" value={oc.height} onChange={(e) => setOc({ ...oc, height: e.target.value })} placeholder="Haut. cm" data-testid="oc-h" className={inpSm} />
            <input type="number" value={oc.quantity} onChange={(e) => setOc({ ...oc, quantity: e.target.value })} placeholder="Qté" data-testid="oc-q" className={inpSm} />
          </div>
          <button onClick={addOc} data-testid="oc-add" className="mt-2 w-full py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 inline-flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" /> Ajouter une chute</button>
          <div className="mt-2 space-y-1.5">
            {(mat.offcuts || []).map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-1.5" data-testid={`oc-item-${o.id}`}>
                <span className="text-slate-600">{o.width} × {o.height} cm · ×{o.quantity}</span>
                <button onClick={() => delOc(o.id)} className="p-1 rounded hover:bg-rose-100 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={History} title="Historique des mouvements">
          <div className="space-y-1.5">
            {hist.movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm border-b border-slate-50 py-1.5">
                <div><div className="text-slate-700">{m.reason}</div><div className="text-[11px] text-slate-400">{(m.created_at || "").slice(0, 10)}</div></div>
                <span className={`font-semibold tabular ${m.quantity >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.quantity >= 0 ? "+" : ""}{m.quantity} {mat.unit}</span>
              </div>
            ))}
            {hist.movements.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Aucun mouvement enregistré.</p>}
          </div>
        </Section>
      </div>
    </div>
  );
}

const Field = ({ label, full, children }) => (<div className={full ? "col-span-2" : ""}><label className="text-xs font-medium text-slate-500">{label}</label><div className="mt-1">{children}</div></div>);
const Section = ({ icon: Icon, title, children }) => (<div className="mt-5"><div className="flex items-center gap-2 text-sm font-semibold text-navy mb-2"><Icon className="w-4 h-4 text-cognac-500" /> {title}</div>{children}</div>);
const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cognac-400 bg-white";
const inpSm = "w-full px-2.5 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cognac-400";
